/**
 * Convert bytes to a human-readable format (KB, MB, GB, etc.)
 *
 * @param {number} sizeInBytes - Size in bytes to convert
 * @param {number} [decimalPlaces=2] - Number of decimal places to round to. Defaults to 2.
 * @returns {string} Human-readable file size string
 *
 * @example
 * convertBytesToHumanReadable(1500)      // returns "1.46 KB"
 * convertBytesToHumanReadable(1500000)   // returns "1.43 MB"
 * convertBytesToHumanReadable(123456789, 0) // returns "118 MB"
 */
export const convertBytesToHumanReadable = (sizeInBytes, decimalPlaces = 2) => {
  if (!sizeInBytes) return '0 Bytes';

  if (typeof sizeInBytes !== 'number' || sizeInBytes < 0) {
    throw new Error('Size must be a non-negative number');
  }

  if (sizeInBytes === 0) {
    return '0 Bytes';
  }

  const sizeUnits = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let unitIndex = 0;
  let size = sizeInBytes;

  while (size >= 1024 && unitIndex < sizeUnits.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Round to specified decimal places
  const roundedSize = size.toFixed(decimalPlaces);
  // Remove trailing .00 if decimal places are zero
  const formattedSize =
    decimalPlaces > 0
      ? roundedSize.replace(/\.?0+$/, '')
      : Math.round(size).toString();

  return `${formattedSize} ${sizeUnits[unitIndex]}`;
};
